<template>
  <div
    class="vdp-datepicker"
    :class="{ 'vdp-datepicker--disabled': disabledPicker }"
  >
    <div class="row">
      <input
        :id="id"
        v-fusioninputcheck="{
          type: 'string',
          tip: $t('hint.inputTip.string'),
          required,
        }"
        class="date-picker-class"
        type="text"
        :name="name"
        :value="formattedValue"
        :placeholder="placeholder"
        :disabled="disabledPicker"
        @focus="showCalendar"
      >
      <span
        v-if="clearButton && selectedDate"
        class="vdp-datepicker__calendar-button clear"
        @click="clearDate"
      >
        <i :class="clearButtonIcon">
          <span
            v-if="clearButtonIcon.length === 0"
            class="fa fa-times fa-lg"
            aria-hidden="true"
          />
        </i>
      </span>
      <span
        v-if="calendarButton"
        class="vdp-datepicker__calendar-button right"
        :class="{ 'disabled': disabledPicker }"
        @click="showCalendar"
      >
        <i :class="calendarButtonIcon">
          <span v-if="calendarButtonIcon.length === 0">&hellip;</span>
        </i>
      </span>
    </div>

    <!-- day view -->
    <div
      v-show="showDayView"
      class="vdp-datepicker__calendar"
      :class="{'placement': canPutTotop && putTotop, 'active': showDayView}"
    >
      <div class="title">
        <div class="title_year">
          {{ currYear }}
        </div>
        <div>{{ formattedTitle }}</div>
      </div>
      <div class="header">
        <span
          style="width: 20%;"
          class="prev"
          :class="{ 'disabled' : previousMonthDisabled(currDate) }"
          @click="previousMonth"
        >
          &lt;
        </span>
        <span
          style="width: 60%;"
          class="up"
          @click="showMonthCalendar"
        >
          {{ currMonthName }} {{ currYear }}
        </span>
        <span
          style="width: 20%;"
          class="next"
          :class="{ 'disabled' : nextMonthDisabled(currDate) }"
          @click="nextMonth"
        >
          &gt;
        </span>
      </div>
      <div>
        <span
          v-for="d in daysOfWeek"
          class="cell day-header"
        >
          {{ d }}
        </span>
        <span
          v-for="bd in blankDays"
          :key="bd"
          class="cell"
        />
        <span
          v-for="day in days"
          :key="day.timestamp"
          class="cell day"
          :class="{ 'selected':day.isSelected, 'disabled':day.isDisabled, 'highlighted': day.isHighlighted, 'today': day.isToday}"
          @click="selectDate(day)"
        >
          {{ day.date }}
        </span>
      </div>
    </div>

    <!-- Month view -->
    <div
      v-show="showMonthView"
      class="vdp-datepicker__calendar"
      :class="{'placement': canPutTotop && putTotop, 'active': showMonthView}"
    >
      <div class="title">
        <div class="title_year">
          {{ currYear }}
        </div>
        <div>{{ formattedTitle }}</div>
      </div>
      <div class="header">
        <span
          style="width: 20%;"
          class="prev"
          :class="{ 'disabled' : previousYearDisabled(currDate) }"
          @click="previousYear"
        >
          &lt;
        </span>
        <span
          style="width: 60%;"
          class="up"
          @click="showYearCalendar"
        >
          {{ getYear() }}
        </span>
        <span
          style="width: 20%;"
          class="next"
          :class="{ 'disabled' : nextYearDisabled(currDate) }"
          @click="nextYear"
        >
          &gt;
        </span>
      </div>
      <span
        v-for="month in months"
        :key="month.timestamp"
        class="cell month"
        track-by="timestamp"
        :class="{ 'selected': month.isSelected, 'disabled': month.isDisabled }"
        @click.stop="selectMonth(month)"
      >
        {{ month.month }}
      </span>
    </div>

    <!-- Year View -->
    <div
      v-show="showYearView"
      class="vdp-datepicker__calendar"
      :style="calendarStyle"
      :class="{'placement': canPutTotop && putTotop, 'active': showYearView}"
    >
      <div class="title">
        <div class="title_year">
          {{ currYear }}
        </div>
        <div>{{ formattedTitle }}</div>
      </div>
      <div class="header">
        <span
          style="width: 20%;"
          class="prev"
          :class="{ 'disabled' : previousDecadeDisabled(currDate) }"
          @click="previousDecade"
        >
          &lt;
        </span>
        <span
          style="width: 60%;"
          class="up"
        >
          {{ getDecade() }}
        </span>
        <span
          style="width: 20%;"
          class="next"
          :class="{ 'disabled' : nextMonthDisabled(currDate) }"
          @click="nextDecade"
        >
          &gt;
        </span>
      </div>
      <span
        v-for="year in years"
        :key="year.timestamp"
        class="cell year"
        track-by="timestamp"
        :class="{ 'selected': year.isSelected, 'disabled': year.isDisabled }"
        @click.stop="selectYear(year)"
      >
        {{ year.year }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DateUtils from '@/utils/datePicker'
import dateUtil from '@/utils/dateUtil'
import { langs } from '@/utils/i18n'
import { utils as inputChangeUtils } from '@/plugin/InputChange'

export default {
  model: {
    prop: 'value',
    event: 'selected',
  },
  props: {
    value: {
      validator (val) {
        return val === null || val instanceof Date || typeof val === 'string'
      },
      default: undefined,
    },
    name: {
      type: String,
      default: undefined,
    },
    id: {
      type: String,
      default: undefined,
    },
    format: {
      type: String,
      default: 'DD MMM YY',
    },
    language: {
      type: String,
      default: 'en',
    },
    /*
    * Disable optional dates
    * @param {Object} disabled - disable date range
    * @param {Date} disabled.from The until date that allows selection on the calendar
    * @param {Date} disabled.to The start date that allows selection on the calendar
    */
    disabled: {
      type: Object,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    inline: {
      type: Boolean,
    },
    clearButton: {
      type: Boolean,
      default: true,
    },
    clearButtonIcon: {
      type: String,
      default: '',
    },
    calendarButton: {
      type: Boolean,
      default: false,
    },
    calendarButtonIcon: {
      type: String,
      default: '',
    },
    disabledPicker: {
      type: Boolean,
      default: false,
    },
    initialView: {
      type: String,
      default: 'day',
    },
    required: {
      type: Boolean,
      default: false,
    },
    dirtyCheckNode: String,
    dirtyCheck: {
      type: Boolean,
      default: false,
    },
    canPutTotop: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      /*
       * Vue cannot observe changes to a Date Object so date must be stored as a timestamp
       * This represents the first day of the current viewing month
       * {Number}
       */
      // currDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime(),
      currDate: this.getDefaultDate(),
      /*
       * Selected Date
       * {Date}
       */
      selectedDate: null,
      /*
       * Flags to show calendar views
       * {Boolean}
       */
      showDayView: false,
      showMonthView: false,
      showYearView: false,
      /*
       * Positioning
       */
      calendarHeight: 0,
      calendarInfo: {
        [langs.enUS]: {
          language: 'English',
          months: {
            original: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            abbr: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          },
          days: {
            original: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
            abbr: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          },
        },
        [langs.zhCN]: {
          language: '中文',
          months: {
            original: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
            abbr: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          },
          days: {
            original: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
            abbr: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          },
        },
      },
      putTotop: false,
    }
  },
  computed: {
    ...mapGetters('language', ['currentLanguage']),
    formattedValue () {
      if (!this.selectedDate) {
        return null
      }
      const formatByLang = dateUtil.getFormatByLang(this.format)
      return DateUtils.formatDate(new Date(this.selectedDate), formatByLang, this.calendarInfo[this.currentLanguage])
    },
    formattedTitle () {
      if (!this.selectedDate) {
        return null
      }
      return this.calendarInfo[this.currentLanguage].days.original[this.selectedDate.getDay()] + ', ' + DateUtils.formatDate(new Date(this.selectedDate), 'MMM DD', this.calendarInfo[this.currentLanguage])
    },
    currMonthName () {
      const d = new Date(this.currDate)
      return DateUtils.getMonthNameAbbr(d.getMonth(), this.calendarInfo[this.currentLanguage].months.abbr)
    },
    currYear () {
      const d = new Date(this.currDate)
      return d.getFullYear()
    },
    /**
     * Returns the day number of the week less one for the first of the current month
     * Used to show amount of empty cells before the first in the day calendar layout
     * @return {Number}
     */
    blankDays () {
      const d = new Date(this.currDate)
      const dObj = new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
      if (this.mondayFirst) {
        return dObj.getDay() > 0 ? dObj.getDay() - 1 : 6
      }
      return dObj.getDay()
    },
    daysOfWeek () {
      return this.calendarInfo[this.currentLanguage].days.abbr
    },
    days () {
      const d = new Date(this.currDate)
      const days = []
      // set up a new date object to the beginning of the current 'page'
      const dObj = new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
      const daysInMonth = DateUtils.daysInMonth(dObj.getFullYear(), dObj.getMonth())
      for (let i = 0; i < daysInMonth; i += 1) {
        days.push({
          date: dObj.getDate(),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedDate(dObj),
          isDisabled: this.isDisabledDate(dObj),
          isHighlighted: this.isHighlightedDate(dObj),
          isToday: dObj.toDateString() === (new Date()).toDateString(),
        })
        dObj.setDate(dObj.getDate() + 1)
      }
      return days
    },
    months () {
      const d = new Date(this.currDate)
      const months = []
      // set up a new date object to the beginning of the current 'page'
      const dObj = new Date(d.getFullYear(), 0, d.getDate(), d.getHours(), d.getMinutes())
      for (let i = 0; i < 12; i += 1) {
        months.push({
          month: DateUtils.getMonthName(i, this.calendarInfo[this.currentLanguage].months.original),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedMonth(dObj),
          isDisabled: this.isDisabledMonth(dObj),
        })
        dObj.setMonth(dObj.getMonth() + 1)
      }
      return months
    },
    years () {
      const d = new Date(this.currDate)
      const years = []
      const dObj = new Date(Math.floor(d.getFullYear() / 10) * 10,
        d.getMonth(), d.getDate(), d.getHours(), d.getMinutes())
      for (let i = 0; i < 10; i += 1) {
        years.push({
          year: dObj.getFullYear(),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedYear(dObj),
          isDisabled: this.isDisabledYear(dObj),
        })
        dObj.setFullYear(dObj.getFullYear() + 1)
      }
      return years
    },
    calendarStyle () {
      const styles = {}

      if (this.isInline) {
        styles.position = 'static'
      }

      return styles
    },
    isOpen () {
      return this.showDayView || this.showMonthView || this.showYearView
    },
    isInline () {
      return typeof this.inline !== 'undefined' && this.inline
    },
  },
  watch: {
    value (value) {
      this.setValue(value)
    },
    disabled (value) {
      this.resetDefaultDate()
    },
  },
  mounted () {
    this.init()
    if (this.dirtyCheck) {
      this.$on('input', () => {
        inputChangeUtils.setDirty(this.dirtyCheckNode)
      })
    }
  },
  destroyed () {
    if (this.dirtyCheck) {
      this.$off('input')
    }
  },
  methods: {
    /**
     * Close all calendar layers
     */
    close () {
      this.showDayView = false
      this.showMonthView = false
      this.showYearView = false
      this.putTotop = false
      this.$emit('closed')
      document.removeEventListener('click', this.clickOutside, false)
    },
    getDefaultDate () {
      const now = new Date().getTime()
      const oneDayMilisec = dateUtil.ONE_DAY_MILLI
      if (this.disabled && this.disabled.to && now < this.disabled.to.getTime() + oneDayMilisec) {
        const disabledToDate = new Date(this.disabled.to.getTime() + oneDayMilisec)
        return new Date(disabledToDate.getFullYear(), disabledToDate.getMonth(), 1).getTime()
      } else if (this.disabled && this.disabled.from && now > this.disabled.from.getTime() - oneDayMilisec) {
        const disabledFromDate = new Date(this.disabled.from.getTime() - oneDayMilisec)
        return new Date(disabledFromDate.getFullYear(), disabledFromDate.getMonth(), 1).getTime()
      } else {
        return new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime()
      }
    },
    resetDefaultDate () {
      if (this.selectedDate === null) {
        this.currDate = this.getDefaultDate()
        return
      }
      this.currDate = new Date(
        this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1).getTime()
    },
    /**
     * Effectively a toggle to show/hide the calendar
     * @return {[type]} [description]
     */
    showCalendar () {
      if (this.disabledPicker) {
        return
      }
      $('.date-picker-class').blur()
      if (this.isOpen) {
        this.close()
        return
      }
      switch (this.initialView) {
        case 'year':
          this.showYearCalendar()
          break
        case 'month':
          this.showMonthCalendar()
          break
        default:
          this.showDayCalendar()
          break
      }
    },
    showDayCalendar () {
      this.close()
      this.getPostion()
      this.showDayView = true
      this.$emit('opened')
      document.addEventListener('click', this.clickOutside, false)
    },
    showMonthCalendar () {
      this.close()
      this.getPostion()
      this.showMonthView = true
      document.addEventListener('click', this.clickOutside, false)
    },
    showYearCalendar () {
      this.close()
      this.getPostion()
      this.showYearView = true
      document.addEventListener('click', this.clickOutside, false)
    },

    setDate (timestamp) {
      this.selectedDate = new Date(timestamp)
      this.currDate = new Date(
        this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1).getTime()
      this.$emit('selected', new Date(timestamp))
      this.$emit('input', new Date(timestamp))
    },

    clearDate () {
      this.selectedDate = null
      this.$emit('selected', null)
      this.$emit('input', null)
      this.$emit('cleared')
    },

    resetValue (val) {
      if (!val || isNaN(Date.parse(val))) {
        this.clearDate()
        this.setValue(null)
      } else {
        const timestamp = Date.parse(val)
        this.setDate(timestamp)
      }
    },

    /**
     * @param {Object} day
     */
    selectDate (day) {
      if (day.isDisabled) {
        return
      }
      this.setDate(day.timestamp)
      if (this.isInline) {
        this.showDayCalendar()
        return
      }
      this.$emit('changedDay', day)
      this.close()
    },

    /**
     * @param {Object} month
     */
    selectMonth (month) {
      if (month.isDisabled) {
        return
      }
      this.currDate = month.timestamp
      this.showDayCalendar()
      this.$emit('changedMonth', month)
    },

    /**
     * @param {Object} year
     */
    selectYear (year) {
      if (year.isDisabled) {
        return
      }
      this.currDate = year.timestamp
      this.showMonthCalendar()
      this.$emit('changedYear', year)
    },

    /**
     * @return {Number}
     */
    getMonth () {
      const d = new Date(this.currDate)
      return d.getMonth()
    },

    /**
     * @return {Number}
     */
    getYear () {
      const d = new Date(this.currDate)
      return d.getFullYear()
    },

    /**
     * @return {String}
     */
    getDecade () {
      const d = new Date(this.currDate)
      const sD = Math.floor(d.getFullYear() / 10) * 10
      return `${sD}'s`
    },

    previousMonth () {
      if (this.previousMonthDisabled()) {
        return
      }
      const d = new Date(this.currDate)
      d.setMonth(d.getMonth() - 1)
      this.currDate = d.getTime()
      this.$emit('changedMonth', d)
    },

    previousMonthDisabled () {
      if (typeof this.disabled === 'undefined' ||
          typeof this.disabled.to === 'undefined' ||
          !this.disabled.to) {
        return false
      }
      const d = new Date(this.currDate)
      if (
        this.disabled.to.getMonth() >= d.getMonth() &&
        this.disabled.to.getFullYear() >= d.getFullYear()
      ) {
        return true
      }
      return false
    },

    nextMonth () {
      if (this.nextMonthDisabled()) {
        return
      }
      const d = new Date(this.currDate)
      const daysInMonth = DateUtils.daysInMonth(d.getFullYear(), d.getMonth())
      d.setDate(d.getDate() + daysInMonth)
      this.currDate = d.getTime()
      this.$emit('changedMonth', d)
    },

    nextMonthDisabled () {
      if (typeof this.disabled === 'undefined' ||
          typeof this.disabled.from === 'undefined' ||
          !this.disabled.from) {
        return false
      }
      const d = new Date(this.currDate)
      if (
        this.disabled.from.getMonth() <= d.getMonth() &&
        this.disabled.from.getFullYear() <= d.getFullYear()
      ) {
        return true
      }
      return false
    },

    previousYear () {
      if (this.previousYearDisabled()) {
        return
      }
      const d = new Date(this.currDate)
      d.setYear(d.getFullYear() - 1)
      this.currDate = d.getTime()
      this.$emit('changedYear')
    },

    previousYearDisabled () {
      if (typeof this.disabled === 'undefined' || typeof this.disabled.to === 'undefined' || !this.disabled.to) {
        return false
      }
      const d = new Date(this.currDate)
      if (this.disabled.to.getFullYear() >= d.getFullYear()) {
        return true
      }
      return false
    },

    nextYear () {
      if (this.nextYearDisabled()) {
        return
      }
      const d = new Date(this.currDate)
      d.setYear(d.getFullYear() + 1)
      this.currDate = d.getTime()
      this.$emit('changedYear')
    },

    nextYearDisabled () {
      if (typeof this.disabled === 'undefined' || typeof this.disabled.from === 'undefined' || !this.disabled.from) {
        return false
      }
      const d = new Date(this.currDate)
      if (this.disabled.from.getFullYear() <= d.getFullYear()) {
        return true
      }
      return false
    },

    previousDecade () {
      if (this.previousDecadeDisabled()) {
        return
      }
      const d = new Date(this.currDate)
      d.setYear(d.getFullYear() - 10)
      this.currDate = d.getTime()
      this.$emit('changedDecade')
    },

    previousDecadeDisabled () {
      if (typeof this.disabled === 'undefined' || typeof this.disabled.to === 'undefined' || !this.disabled.to) {
        return false
      }
      const d = new Date(this.currDate)
      if (Math.floor(this.disabled.to.getFullYear() / 10) * 10 >=
           Math.floor(d.getFullYear() / 10) * 10) {
        return true
      }
      return false
    },

    nextDecade () {
      if (this.nextDecadeDisabled()) {
        return
      }
      const d = new Date(this.currDate)
      d.setYear(d.getFullYear() + 10)
      this.currDate = d.getTime()
      this.$emit('changedDecade')
    },

    nextDecadeDisabled () {
      if (typeof this.disabled === 'undefined' || typeof this.disabled.from === 'undefined' || !this.disabled.from) {
        return false
      }
      const d = new Date(this.currDate)
      if (Math.ceil(this.disabled.from.getFullYear() / 10) * 10 <=
           Math.ceil(d.getFullYear() / 10) * 10) {
        return true
      }
      return false
    },

    /**
     * Whether a day is selected
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedDate (dObj) {
      return this.selectedDate && this.selectedDate.toDateString() === dObj.toDateString()
    },

    /**
     * Whether a day is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledDate (date) {
      let disabled = false

      if (typeof this.disabled === 'undefined') {
        return false
      }

      if (typeof this.disabled.dates !== 'undefined') {
        this.disabled.dates.forEach((d) => {
          if (date.toDateString() === d.toDateString()) {
            disabled = true
          }
        })
      }
      if (typeof this.disabled.to !== 'undefined' && this.disabled.to && date < this.disabled.to) {
        disabled = true
      }
      if (typeof this.disabled.from !== 'undefined' && this.disabled.from && date > this.disabled.from) {
        disabled = true
      }
      if (typeof this.disabled.days !== 'undefined' && this.disabled.days.indexOf(date.getDay()) !== -1) {
        disabled = true
      }
      return disabled
    },

    /**
     * Whether a day is highlighted (only if it is not disabled already)
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightedDate (date) {
      if (this.isDisabledDate(date)) {
        return false
      }

      let highlighted = false

      if (typeof this.highlighted === 'undefined') {
        return false
      }

      if (typeof this.highlighted.dates !== 'undefined') {
        this.highlighted.dates.forEach((d) => {
          if (date.toDateString() === d.toDateString()) {
            highlighted = true
            return true
          }
          return false
        })
      }

      if (this.isDefined(this.highlighted.from) && this.isDefined(this.highlighted.to)) {
        highlighted = date >= this.highlighted.from && date <= this.highlighted.to
      }

      if (typeof this.highlighted.days !== 'undefined' && this.highlighted.days.indexOf(date.getDay()) !== -1) {
        highlighted = true
      }
      return highlighted
    },

    /**
     * Helper
     * @param  {mixed}  prop
     * @return {Boolean}
     */
    isDefined (prop) {
      return typeof prop !== 'undefined' && prop
    },

    /**
     * Whether the selected date is in this month
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedMonth (date) {
      return (this.selectedDate &&
        this.selectedDate.getFullYear() === date.getFullYear() &&
        this.selectedDate.getMonth() === date.getMonth())
    },

    /**
     * Whether a month is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledMonth (date) {
      let disabled = false

      if (typeof this.disabled === 'undefined') {
        return false
      }

      if (typeof this.disabled.to !== 'undefined' && this.disabled.to) {
        const dateInvalid = date.getMonth() < this.disabled.to.getMonth() &&
                            date.getFullYear() <= this.disabled.to.getFullYear()
        if (dateInvalid || date.getFullYear() < this.disabled.to.getFullYear()) {
          disabled = true
        }
      }
      if (typeof this.disabled.from !== 'undefined' && this.disabled.from) {
        if (this.disabled.from) {
          const validDateVals = (date.getMonth() > this.disabled.from.getMonth() &&
            date.getFullYear() >= this.disabled.from.getFullYear())
          if (validDateVals || date.getFullYear() > this.disabled.from.getFullYear()) {
            disabled = true
          }
        }
      }
      return disabled
    },

    /**
     * Whether a year is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedYear (date) {
      return this.selectedDate && this.selectedDate.getFullYear() === date.getFullYear()
    },

    /**
     * Whether a month is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledYear (date) {
      let disabled = false
      if (typeof this.disabled === 'undefined' || !this.disabled) {
        return false
      }

      if (typeof this.disabled.to !== 'undefined' && this.disabled.to) {
        if (date.getFullYear() < this.disabled.to.getFullYear()) {
          disabled = true
        }
      }
      if (typeof this.disabled.from !== 'undefined' && this.disabled.from) {
        if (date.getFullYear() > this.disabled.from.getFullYear()) {
          disabled = true
        }
      }

      return disabled
    },

    /**
     * Set the datepicker value
     * @param {Date|String|null} date
     */
    setValue (date) {
      if (typeof date === 'string') {
        const parsed = new Date(dateUtil.parse(date, this.format))
        date = isNaN(parsed.valueOf()) ? null : parsed // eslint-disable-line no-param-reassign
      }
      if (!date) {
        const d = new Date()
        this.currDate = new Date(d.getFullYear(), d.getMonth(), 1).getTime()
        this.selectedDate = null
        return
      }
      this.selectedDate = date
      this.currDate = new Date(date.getFullYear(), date.getMonth(), 1).getTime()
    },

    /**
     * Close the calendar if clicked outside the datepicker
     * @param  {Event} event
     */
    clickOutside (event) {
      if (this.$el && !this.$el.contains(event.target)) {
        if (this.isInline) {
          return this.showDayCalendar()
        }
        this.resetDefaultDate()
        this.close()
        document.removeEventListener('click', this.clickOutside, false)
      }
      return null
    },

    getPostion () {
      const windowHeight = $(window).height()
      const offsetTop = this.$el.getBoundingClientRect().top
      const selfHeight = $(this.$el).height() + $(this.$el).find('.vdp-datepicker__calendar').height()
      let distanceToBottom = windowHeight - offsetTop - selfHeight
      this.putTotop = distanceToBottom < 0
      this.calcBothSides()
    },

    calcBothSides () {
      const clientWidth = document.body.clientWidth
      const rect = this.$el.getBoundingClientRect()
      const x = rect.x || rect.left
      const calendarLeft = x + rect.width + $(this.$el).find('.vdp-datepicker__calendar').width() / 2
      const calendarRight = x + rect.width - $(this.$el).find('.vdp-datepicker__calendar').width() / 2
      const $dom = $($(this.$el).find('.vdp-datepicker__calendar'))
      if (calendarLeft > clientWidth) {
        $dom.css('right', 0)
      } else if (calendarRight < 0) {
        $dom.css('left', 0)
      }
    },

    init () {
      if (this.value) {
        this.setValue(this.value)
      }
      if (this.isInline) {
        this.showDayCalendar()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.row {
  padding: 0 0;
  margin: 0 0;
}
input {
  width: 70%;
  margin: 0;
  background-color: transparent;
  cursor: default;
  display: inline;
  border: none 0 transparent;
  box-shadow: none;
}
input:disabled {
  color: #0C3A58;
}
[type='text']:focus, [type='date']:focus, [type='datetime']:focus, [type='datetime-local']:focus, [type='month']:focus, [type='week']:focus {
    border: none;
    border-right: none;
    background-color: transparent;
    box-shadow: none;
}
.vdp-datepicker {
  width: 180px;
  position: relative;
  text-align: left;
  box-sizing: border-box;
  margin: 0 0;
  border: solid 1px #B5B5B5;
  height: 40px;
  background-color: #ffffff;
  font-weight: normal;
  .day-header {
    width: calc(100%/7 - 10px);
    margin: 0 5px;
    font-size: 75%;
    white-space: no-wrap;
    cursor: inherit;
    font-family: Roboto;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
    color: rgba(0, 0, 0, 0.38);
  }

  .day {
    font-family: Roboto;
    font-size: 12px;
    text-align: center;
    color: rgba(0, 0, 0, 0.87);
  }

  .header span {
    display: inline-block;
    text-align: center;
    font-size: 14px;
    width: calc(100%/7);
  }
}
.vdp-datepicker--disabled {
  background: #ededed;
  pointer-events: none;
  border-color: #B5B5B5;
  opacity: 0.5;
}
.vdp-datepicker__calendar {
  position: absolute;
  z-index: 100;
  width: 240px;
  border-radius: 8px;
  background-color: #ffffff;
  border: solid 1px #5f7988;
}
.placement {
  bottom: 40px;
}
.vdp-datepicker__calendar-button {
  float: right;
  margin: 10px 5px;
  cursor: pointer;
}
.title {
  background: #1c5a83;
  height: 66px;
  padding: 15px;
  color: white;
  border-radius: 8px 8px 0 0;
}
.title_year {
  font-weight: normal;
  color: #87a2b1;
  line-height: 1;
}
.header {
  display: block;
  width: 100%;
  line-height: 40px;
}
.up:not(.disabled) {
  width: 50%;
  text-align: center;
  cursor: pointer;
  &:hover {
    background: #1c5a83;
    color: #ffffff;
  }
}

.prev, .next {
  width: 20%;
  position: relative;
  text-align: center;
  &:not(.disabled){
    &:hover {
      background: #1c5a83;
      color: #ffffff;
    }
  }
}
.prev {
  float: left;
}
.next {
  float: right;
}
.cell {
  float: left;
  margin: 5px;
  width: calc(100%/7 - 10px);
  height: 24px;
  line-height: 24px;
  text-align: center;
  font-size: 12px;
  vertical-align: middle;
  border: 1px solid transparent;
  &:not(.blank):not(.disabled).day,
  &:not(.blank):not(.disabled).month,
  &:not(.blank):not(.disabled).year {
    cursor: pointer;
    &:hover {
      border-radius: 12px;
      background-color: #1c5a83;
      color: #ffffff!important;
    }
  }
}
.selected {
  background-color: #1c5a83;
  text-align: center;
  color: #ffffff!important;
  border-radius: 12px;
  &:hover {
    background-color: #1c5a76;
  }
}
.disabled {
  color: rgba(0, 0, 0, 0.38)!important;
  cursor: default;
}
.highlighted {
  background-color: #1c5a83!important;
}

.month, .year {
  width: calc(100%/3 - 30px);
  margin:  5px 15px;
  height: 30px;
  line-height: 30px;
  border-radius: 20px;
  &:not(.blank):not(.disabled).cell{
    &:hover {
      border-radius: 20px;
    }
  }

}
.date-picker-class {
  cursor: pointer;
  width: calc(100% - 40px);
  padding: 8px 0 8px 10px;
  height: 38px;
}
.clear {
  right: 20px;
  position: absolute;
  cursor: pointer;
}

.right {
  position: absolute;
  right: 0px;
}

.prev, .up, .next {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>
